import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveModal } from "@/components/ios/responsive-modal";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { Plus, Users, Mail, Phone, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { logActivity } from "@/lib/activity.functions";

export const Route = createFileRoute("/_authenticated/app/clients")({
  component: ClientsPage,
});

type Client = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  vat: string | null;
  fiscal_code: string | null;
  type: string | null;
  address: string | null;
  notes: string | null;
};

const EMPTY = { name: "", email: "", phone: "", vat: "", fiscal_code: "", address: "", notes: "" };

function ClientsPage() {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const logFn = useServerFn(logActivity);

  const load = async () => {
    const { data, error } = await supabase.from("clients").select("*").order("name");
    if (error) toast.error(error.message);
    setClients((Array.isArray(data) ? data : []) as Client[]);
  };

  useEffect(() => {
    load();
  }, []);

  const { pull, refreshing } = usePullToRefresh(load, { threshold: 64 });

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c: Client) => {
    setEditingId(c.id);
    setForm({
      name: c.name ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      vat: c.vat ?? "",
      fiscal_code: c.fiscal_code ?? "",
      address: c.address ?? "",
      notes: c.notes ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("pages.clients.nameRequired"));
    if (form.vat && !/^[A-Z]{2}?\d{11}$/i.test(form.vat.replace(/\s/g, ""))) {
      return toast.error(t("pages.clients.vatInvalid"));
    }
    if (form.fiscal_code && !/^[A-Z0-9]{16}$/i.test(form.fiscal_code.trim())) {
      return toast.error(t("pages.clients.fiscalCodeInvalid"));
    }
    const payload = {
      name: form.name.trim(),
      email: form.email || null,
      phone: form.phone || null,
      vat: form.vat || null,
      fiscal_code: form.fiscal_code || null,
      address: form.address || null,
      notes: form.notes || null,
    };
    if (editingId) {
      const { error } = await supabase.from("clients").update(payload).eq("id", editingId);
      if (error) return toast.error(error.message);
      await logFn({ data: { action: "client.update", entity: "client", entity_id: editingId, metadata: { name: payload.name } } }).catch(() => {});
      toast.success(t("pages.clients.updated"));
    } else {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: created, error } = await supabase.from("clients").insert({ ...payload, owner_id: u.user.id }).select("id").single();
      if (error) return toast.error(error.message);
      await logFn({ data: { action: "client.create", entity: "client", entity_id: created?.id ?? null, metadata: { name: payload.name } } }).catch(() => {});
      toast.success(t("pages.clients.created"));
    }
    setForm(EMPTY);
    setEditingId(null);
    setOpen(false);
    load();
  };

  const remove = async (c: Client) => {
    if (!confirm(t("pages.clients.deleteConfirm", { name: c.name }))) return;
    const { error } = await supabase.from("clients").delete().eq("id", c.id);
    if (error) return toast.error(error.message);
    await logFn({ data: { action: "client.delete", entity: "client", entity_id: c.id, metadata: { name: c.name } } }).catch(() => {});
    toast.success(t("pages.clients.deleted"));
    load();
  };

  return (
    <>
      <PageHeader
        title={t("pages.clients.title")}
        description={t("pages.clients.description")}
        actions={
          <Button className="gap-2 ios-tap" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t("pages.clients.newClient")}
          </Button>
        }
      />
      {(pull > 0 || refreshing) && (
        <div className="flex justify-center pt-2 text-muted-foreground" style={{ height: Math.max(pull, refreshing ? 32 : 0) }}>
          <Loader2 className={"h-5 w-5 " + (refreshing ? "animate-spin" : "")} />
        </div>
      )}
      <PageBody>
        {clients === null ? (
          <Card className="flex items-center justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </Card>
        ) : clients.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-16 text-center">
            <Users className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="font-display text-xl font-semibold">{t("pages.clients.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.clients.emptyDesc")}</p>
            <Button className="mt-4 gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" /> {t("pages.clients.createClient")}
            </Button>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {clients.map((c) => (
              <Card key={c.id} className="group p-5 ios-tap relative">
                <div className="font-semibold pr-16">{c.name}</div>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {c.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {c.email}</div>}
                  {c.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.phone}</div>}
                  {c.vat && <div className="font-mono text-xs">{t("pages.clients.fVat")}: {c.vat}</div>}
                  {c.fiscal_code && <div className="font-mono text-xs">{t("pages.clients.fFiscalCode")}: {c.fiscal_code}</div>}
                </div>
                <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} aria-label={t("pages.clients.edit")}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} aria-label={t("pages.clients.delete")}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </PageBody>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? t("pages.clients.modalEdit") : t("pages.clients.modalNew")}
        footer={<Button onClick={save} className="ios-tap">{editingId ? t("pages.clients.save") : t("pages.clients.createClient")}</Button>}
      >
        <div className="space-y-3">
          <div>
            <Label>{t("pages.clients.fName")} *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>{t("pages.clients.fEmail")}</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>{t("pages.clients.fPhone")}</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("pages.clients.fVat")}</Label>
              <Input value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
            </div>
            <div>
              <Label>{t("pages.clients.fFiscalCode")}</Label>
              <Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>{t("pages.clients.fAddress")}</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label>{t("pages.clients.fNotes")}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
